import { useState } from 'react';

import { Alert, Box, Button, Link, Paper, Typography } from '@mui/material';

import { isWellFormedUnsubscribeToken, unsubscribe } from '@/api/unsubscribe';
import CleanTradeLogoLight from '@/assets/CleanTrade-logo-light.png';
import { RoutePath } from '@/utils/enums';

import { cardSx, logoSx, pageSx } from './Unsubscribe.styles';

type PageState = 'confirm' | 'submitting' | 'done' | 'invalid' | 'failed';

/**
 * The page the Unsubscribe link in alert email opens (RME-1362). It runs
 * signed out: `main.tsx` renders it outside Auth0 and the Redux store.
 *
 * Opening the page changes nothing. Email security scanners open every link
 * in an email, so only the button turns off Email Notifications.
 *
 * The token is in the fragment (`#token=…`), which the browser never sends to
 * the server, so it stays out of the CloudFront and WAF logs.
 */
export function Unsubscribe() {
  const token = new URLSearchParams(globalThis.location.hash.slice(1)).get(
    'token'
  );
  const [state, setState] = useState<PageState>(() =>
    isWellFormedUnsubscribeToken(token) ? 'confirm' : 'invalid'
  );

  const handleUnsubscribe = async () => {
    setState('submitting');
    const result = await unsubscribe(token as string);
    setState(result === 'unsubscribed' ? 'done' : result);
  };

  const manageAlertsLink = (
    <Link href={RoutePath.SETTINGS_ALERTS}>Manage your alerts.</Link>
  );

  return (
    <Box sx={pageSx}>
      <Box
        component="img"
        src={CleanTradeLogoLight}
        alt="CleanTrade"
        sx={logoSx}
      />
      <Paper variant="outlined" sx={cardSx}>
        {state === 'done' && (
          <>
            <Typography variant="h1">Unsubscribed</Typography>
            <Typography>You will no longer receive email alerts.</Typography>
          </>
        )}
        {state === 'invalid' && (
          <>
            <Typography variant="h1">This link is not valid</Typography>
            <Typography>Sign in to manage your alerts.</Typography>
            {manageAlertsLink}
          </>
        )}
        {(state === 'confirm' ||
          state === 'submitting' ||
          state === 'failed') && (
          <>
            <Typography variant="h1">Unsubscribe from all emails?</Typography>
            <Typography>
              This deactivates all trading activity, market activity, and
              position management alerts.
            </Typography>
            {state === 'failed' && (
              <Alert severity="error">Something went wrong. Try again.</Alert>
            )}
            <Box>
              <Button
                variant="contained"
                size="large"
                onClick={handleUnsubscribe}
                disabled={state === 'submitting'}
              >
                Unsubscribe
              </Button>
            </Box>
            {manageAlertsLink}
          </>
        )}
      </Paper>
    </Box>
  );
}

export default Unsubscribe;
